import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { LOCALE_STORAGE_KEY } from "./localeConstants";
import { DOMESTIC_LOCALE, getLanguageMeta, isDomesticLocale, SUPPORTED_LANGUAGE_CODES } from "./languages";
import { translations } from "./translations";

const DEFAULT_LANGUAGE = DOMESTIC_LOCALE;

const defaultLocale = {
  language: DEFAULT_LANGUAGE,
  currency: getLanguageMeta(DEFAULT_LANGUAGE).currency,
  preferenceSource: "auto",
};

const LocaleContext = createContext(null);

function loadLocale() {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!raw) return defaultLocale;
    const parsed = { ...defaultLocale, ...JSON.parse(raw) };
    if (!SUPPORTED_LANGUAGE_CODES.includes(parsed.language)) {
      parsed.language = defaultLocale.language;
    }
    if (parsed.preferenceSource !== "manual") {
      parsed.preferenceSource = "auto";
    }
    const meta = getLanguageMeta(parsed.language);
    parsed.currency = meta.currency;
    return parsed;
  } catch {
    return defaultLocale;
  }
}

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(loadLocale);

  const lang = SUPPORTED_LANGUAGE_CODES.includes(locale.language)
    ? locale.language
    : defaultLocale.language;
  const meta = getLanguageMeta(lang);
  const currency = locale.currency || meta.currency;
  const preferenceSource = locale.preferenceSource || "auto";

  useEffect(() => {
    localStorage.setItem(
      LOCALE_STORAGE_KEY,
      JSON.stringify({ language: lang, currency, preferenceSource })
    );
    document.documentElement.lang = meta.htmlLang;
    document.documentElement.dir = meta.dir;
  }, [lang, currency, preferenceSource, meta.htmlLang, meta.dir]);

  const value = useMemo(() => {
    const dict = translations[lang] || translations.zh;

    const t = (key) =>
      dict[key] ??
      (lang === "zh-hant" ? translations.zh?.[key] : undefined) ??
      translations.en?.[key] ??
      translations.zh[key] ??
      key;

    const updateLocale = (patch) => {
      setLocale((prev) => {
        const next = { ...prev, ...patch };
        if (
          next.language &&
          !SUPPORTED_LANGUAGE_CODES.includes(next.language)
        ) {
          next.language = defaultLocale.language;
        }
        if (next.language) {
          next.currency = getLanguageMeta(next.language).currency;
        }
        return next;
      });
    };

    const formatCurrency = (amount, options = {}) => {
      const value = Number(amount);
      if (!Number.isFinite(value)) return String(amount);
      try {
        return new Intl.NumberFormat(meta.htmlLang, {
          style: "currency",
          currency: options.currency || currency,
          maximumFractionDigits: options.maximumFractionDigits ?? 0,
        }).format(value);
      } catch {
        return `${options.currency || currency} ${value}`;
      }
    };

    return {
      ...locale,
      language: lang,
      currency,
      preferenceSource,
      meta,
      isDomestic: isDomesticLocale(lang),
      t,
      updateLocale,
      formatCurrency,
    };
  }, [locale, lang, currency, preferenceSource, meta]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return ctx;
}
